import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Account } from "model";
import CompanyAccount from "model/CompanyAccount";

/**
 * @class PassThruHistory
 * @classdesc Class definition for record type from model
 * @param {OBJECT} record - record from the model to init class
 * @extends BCMSRecord
 * @example const notification = new PassThruHistory(model.passThruHistory[0]);
 *
 * @property {NUMBER} _id - The id of the history
 * @property {STRING} _createdBy - The createdBy name of the history
 * @property {bcmsDate} _createdDate - The createdDate of the history
 * @property {bcmsDate} _updatedDate - The updatedDate of the notification history
 * @property {STRING} _status - The status of the history
 * @property {STRING} _fileName - The fileName of the history
 * @property {STRING} _fileURL - The fileURL of the history
 * @property {STRING} _isChecked - Component functionality specific property.
 */
export default class PassThruHistory extends BCMSRecord {
  _id;
  _createdBy;
  _createdDate;
  _updatedDate;
  _status;
  _fileName;
  _fileURL;
  _isChecked;
  _companyAccount;
  _participants;
  _creditAmount;
  _debitAmount;
  _secCode;
  _batchNumber;
  _rawFileName;

  constructor(record) {
    super(record);
  }
}

PassThruHistory.define({
  recordType: "passThruHistory",
  recordIDProp: "id",
  className: "PassThruHistory",
  recordProperties: [
    /**
     * @id
     * @instance
     * @property get {NUMBER} - gets the id of the history
     * @property set {NUMBER} - sets the id of the history
     * @memberof PassThruHistory
     */
    BCMSRecordProperty.mapToNumber("id", "_id"),

    /**
     * @createdBy
     * @instance
     * @property get {STRING} - gets the name of the history
     * @property set {STRING} - sets the name of the history
     * @memberof PassThruHistory
     */
    BCMSRecordProperty.mapToString("createdBy", "_createdBy"),

    /**
     * @createdDate
     * @instance
     * @property get {bcmsDate} - gets the createdDate of the history
     * @property set {bcmsDate} - sets the createdDate of the history
     * @memberof PassThruHistory
     */
    BCMSRecordProperty.mapToDate("createdDate", "_createdDate"),

    /**
     * @updatedDate
     * @instance
     * @property get {bcmsDate} - gets the updatedDate of the history
     * @property set {bcmsDate} - sets the updatedDate of the history
     * @memberof PassThruHistory
     */
    BCMSRecordProperty.mapToDate("updatedDate", "_updatedDate"),

    /**
     * @status
     * @instance
     * @property get {STRING} - gets the status of the history
     * @property set {STRING} - sets the status of the history
     * @memberof PassThruHistory
     */
    BCMSRecordProperty.mapToString("status", "_status"),

    /**
     * @fileName
     * @instance
     * @property get {STRING} - gets the fileName of the history
     * @property set {STRING} - sets the fileName of the history
     * @memberof PassThruHistory
     */
    BCMSRecordProperty.mapToString("fileName", "_fileName"),

    /**
     * @fileURL
     * @instance
     * @property get {STRING} - gets the fileURL of the history
     * @property set {STRING} - sets the fileURL of the history
     * @memberof PassThruHistory
     */
    BCMSRecordProperty.mapToString("fileURL", "_fileURL"),

    /**
     * @isChecked
     * @instance
     * @property get {STRING} - gets the isChecked
     * @property set {STRING} - sets the isChecked
     * @memberof PassThruHistory
     */
    BCMSRecordProperty.mapToString("isChecked", "_isChecked"),
    /**
     * @name companyAccount
     * @instance
     * @property get {STRING} - gets the companyAccount value
     * @property set {STRING} - sets the companyAccount value
     * @memberof PassThruPending
     */
    BCMSRecordProperty.mapToClass("companyAccount", "_companyAccount", () => CompanyAccount),

    /**
     * @name participants
     * @instance
     * @property get {ARRAY} - gets the _participants
     * @property set {ARRAY} - sets the _participants
     * @note The mapToArray currently does not work correctly when the toRecord function is called
     * @memberof ACHTemplate
     */
    BCMSRecordProperty.mapTo({
      id: "participants",
      instanceField: "_participants",
      setter: function (value) {
        if (value === undefined || Array.isArray(value)) {
          this._participants = value;
        } else {
          this._participants = value === "" ? [] : value.split(",");
        }
      },
      toRecord: function () {
        return this._participants === undefined || this._participants.length === 0 ? "" : this._participants.reduce((result, item) => `${result},${item}`);
      },
    }),

    /**
     * @name creditAmount
     * @instance
     * @property get {CompanyAccount} - gets the creditAmount
     * @property set {CompanyAccount} - sets the creditAmount
     * @memberof ACHTemplate
     */
    BCMSRecordProperty.mapToCurrency("creditAmount", "_creditAmount"),

    /**
     * @name debitAmount
     * @instance
     * @property get {CompanyAccount} - gets the debitAmount
     * @property set {CompanyAccount} - sets the debitAmount
     * @memberof ACHTemplate
     */
    BCMSRecordProperty.mapToCurrency("debitAmount", "_debitAmount"),

    /**
     * @name secCode
     * @instance
     * @property get {STRING} - gets the secCode
     * @property set {STRING} - sets the secCode
     * @memberof PassThruPending
     */
    BCMSRecordProperty.mapToString("secCode", "_secCode"),

    /**
     * @name batchNumber
     * @instance
     * @property get {STRING} - gets the batchNumber
     * @property set {STRING} - sets the batchNumber
     * @memberof PassThruPending
     */
    BCMSRecordProperty.mapToString("batchNumber", "_batchNumber"),

    /**
     * @name rawFileName
     * @instance
     * @property get {STRING} - gets the rawFileName
     * @property set {STRING} - sets the rawFileName
     * @memberof PassThruPending
     */
    BCMSRecordProperty.mapToString("rawFileName", "_rawFileName"),
    /**
     * @name offsetAccount
     * @instance
     * @property get {STRING} - gets the offsetAccount value
     * @property set {STRING} - sets the offsetAccount value
     * @memberof PassThruPending
     */
    BCMSRecordProperty.mapToClass("offsetAccount", "_offsetAccount", () => Account),
  ],
});
